import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model } from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

/**
 * Radio values are limited to `string | number` rather than an arbitrary
 * object. A radio's value ends up in a native input's `value` attribute and
 * is compared by identity, both of which behave badly for objects. A caller
 * with a list of records passes the id and looks the record back up.
 */
export type BzRadioValue = string | number | null;

let nextGroupId = 0;

/**
 * Groups `bz-radio` children and owns their shared value.
 *
 * The group is the `ControlValueAccessor`, not the individual radio — a form
 * control binds to "which one is selected", which is a property of the set.
 * The legacy console put the accessor on each radio, which meant a form
 * control per option.
 */
@Component({
  selector: 'bz-radio-group',
  templateUrl: './bz-radio-group.html',
  styleUrl: './bz-radio-group.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BzRadioGroup),
      multi: true,
    },
  ],
})
export class BzRadioGroup implements ControlValueAccessor {
  readonly value = model<BzRadioValue>(null);
  readonly disabled = model(false);

  /** Shared `name` for the native inputs, so arrow keys move within one
   * group and not across every radio on the page. */
  readonly name = input(`bz-radio-group-${nextGroupId++}`);

  readonly orientation = input<'vertical' | 'horizontal'>('vertical');

  /**
   * Accessible name for the group. `role="radiogroup"` without one announces
   * as "radio group" and nothing else — the reader hears the options but not
   * the question.
   *
   * Prefer `ariaLabelledby` pointing at the visible heading the group already
   * sits under; a radio group almost always has one, and copying its text
   * into `ariaLabel` is how the two drift apart later.
   */
  readonly ariaLabel = input('');
  readonly ariaLabelledby = input('');

  protected readonly groupClass = computed(
    () => `bz-radio-group bz-radio-group--${this.orientation()}`,
  );

  private propagateChange: (value: BzRadioValue) => void = () => undefined;
  private propagateTouched: () => void = () => undefined;

  writeValue(value: BzRadioValue): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: BzRadioValue) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /** Called by a child radio when it is picked. */
  select(value: BzRadioValue): void {
    if (this.disabled() || this.value() === value) {
      return;
    }

    this.value.set(value);
    this.propagateChange(value);
  }

  markTouched(): void {
    this.propagateTouched();
  }
}
